import { MathMLToLaTeX } from 'mathml-to-latex'
import MyScript, { MyScriptModel } from '@/api/external/MyScript'
import { DrawingModel } from '@/models/DrawingModel'

/**
 * 手書き入力変換-数式
 */
export default class MyScriptMath extends MyScript {
  /**
   * コンストラクタ
   */
  constructor() {
    super()
    // 変換先言語
    this.lang = 'MATHML'
    // 変換先コンテンツタイプ
    this.contentType = 'Math'
    // レスポンスタイプ
    this.accept = 'application/json, application/x-latex'
  }

  /**
   * 変換処理の実行
   *
   * @param canvasWidth キャンバス幅
   * @param canvasHeight キャンバス高さ
   * @param strokes ストローク情報
   * @returns 変換結果(latex文字列)
   */
  public async recognize(
    canvasWidth: number,
    canvasHeight: number,
    strokes: DrawingModel.Stroke[]
  ): Promise<string | null> {
    // キャンバスサイズの更新
    this.canvasWidth = canvasWidth
    this.canvasHeight = canvasHeight

    // リクエストの生成
    const body = this.requestBody(strokes)

    // API実行
    let response = await this.post(this.header, body)
    // 結果が空の場合は終了
    if (response === null || response === undefined) {
      return null
    }

    // レスポンスが数値型の場合は再取得を行う
    //   1.0などの場合に小数点以下が丸められてしまうため
    if (typeof response === 'number') {
      response = await this.postMathMl(this.header, body)
    }

    return response
  }

  /**
   * リクエスト情報生成
   *
   * @param strokes ストローク情報
   * @returns リクエスト情報
   */
  private requestBody(strokes: DrawingModel.Stroke[]): MyScriptModel.RequestBody {
    // デフォルトのリクエスト生成
    const body = this.defaultBody(strokes)

    // 数式モード用のデータを追加
    const configuration: MyScriptConfiguration = {
      math: {
        mimeTypes: ['application/x-latex'],
        solver: {
          enable: false,
        },
      },
    }
    body.configuration = configuration

    return body
  }

  /**
   * 変換処理の実行
   *   MATHML系式で取得し、latex変換して返す
   *   数値型(1.0など)の場合に丸められてしまうため、その際の対処用
   *
   * @param headerBase 元のリクエストヘッダ
   * @param bodyBase 元のリクエストボディ
   * @returns 変換結果(latex文字列)
   */
  private async postMathMl(
    headerBase: MyScriptModel.RequestHeader,
    bodyBase: MyScriptModel.RequestBody
  ): Promise<string | null> {
    // ヘッダをmathml用に書き換え
    const header = headerBase
    header.Accept = 'application/json, application/mathml+xml'
    // ボディをmathml用に書き換え
    const body = bodyBase
    body.configuration = {
      math: {
        mimeTypes: ['application/mathml+xml'],
        solver: {
          enable: false,
        },
      },
    }

    // API実行
    const response = await this.post(header, body)
    // 結果がnullの場合は終了
    if (!response) {
      return null
    }

    // 処理結果の変換を行って返す
    return this.convertResult(response.toString())
  }

  /**
   * 取得したmathmlをlatexに変換する
   *
   * @param mathml mathml形式データ
   * @returns latex形式データ
   */
  private convertResult(mathml: string): string {
    // mathmlをlatexに変換
    return MathMLToLaTeX.convert(mathml)
  }
}

/**
 * API実行時の詳細設定情報(数式用)
 */
type MyScriptConfiguration = MyScriptModel.ConfigurationBase & {
  /** 数学モード用の設定情報 */
  math: MyScriptConfigurationMath
}

/**
 * API実行時の数式関連の詳細設定情報
 */
interface MyScriptConfigurationMath {
  /** レスポンスのMIMEタイプ */
  mimeTypes: string[]
  /** solver設定(用途不明) */
  solver: MyScriptConfigurationSolver
}

/**
 * API実行時のsolver設定(用途不明)
 */
interface MyScriptConfigurationSolver {
  /** フラグ */
  enable: boolean
}
